import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Search, Eye, AlertTriangle, CheckCircle2, Clock,
  Loader2, UserPlus, Filter, ArrowUpDown, ChevronRight
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { usePermissions } from "../../context/PermissionsContext";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const ManagerTeamPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  useEffect(() => {
    api.get("/manager/team").then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout title="Team Roster" variant="manager">
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  const members = data?.members || [];
  const summary = data?.summary || {};
  const departments = [...new Set(members.map(m => m.department).filter(Boolean))];

  let filtered = members.filter(m => {
    const matchSearch = !search || m.full_name.toLowerCase().includes(search.toLowerCase()) || m.email.toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === "all" || m.department === deptFilter;
    return matchSearch && matchDept;
  });

  if (sortBy === "name") filtered.sort((a, b) => a.full_name.localeCompare(b.full_name));
  else if (sortBy === "completion") filtered.sort((a, b) => b.completion_rate - a.completion_rate);
  else if (sortBy === "compliance") filtered.sort((a, b) => b.compliance_overdue - a.compliance_overdue);
  else if (sortBy === "courses") filtered.sort((a, b) => b.total_courses - a.total_courses);

  return (
    <AdminLayout title="Team Roster" variant="manager">
      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show" data-testid="manager-team-page">
        {/* Header */}
        <motion.div variants={item} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" data-testid="team-page-title">Team Roster</h1>
            <p className="text-sm text-muted-foreground mt-1">{summary.team_size || 0} direct reports &middot; {summary.active || 0} active</p>
          </div>
          {hasPermission("enrollments.create") && (
            <Button className="gap-2" onClick={() => navigate("/manager/team-enroll")} data-testid="enroll-team-btn">
              <UserPlus className="w-4 h-4" /> Enroll Team Members
            </Button>
          )}
        </motion.div>

        {/* Summary Cards */}
        <motion.div variants={item} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Team Size", value: summary.team_size, icon: Users, color: "text-blue-600 bg-blue-50" },
            { label: "Total Enrollments", value: summary.total_enrollments, icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
            { label: "Avg Completion", value: `${summary.avg_completion_rate}%`, icon: ArrowUpDown, color: "text-violet-600 bg-violet-50" },
            { label: "Compliance Overdue", value: summary.compliance_overdue, icon: AlertTriangle, color: summary.compliance_overdue > 0 ? "text-red-600 bg-red-50" : "text-emerald-600 bg-emerald-50" },
          ].map((s, i) => (
            <Card key={i} className="border">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", s.color)}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className="text-xl font-bold">{s.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {/* Filters */}
        <motion.div variants={item} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search members..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="team-search-input" />
          </div>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-[180px]" data-testid="dept-filter">
              <Filter className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px]" data-testid="sort-by">
              <ArrowUpDown className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="completion">Completion Rate</SelectItem>
              <SelectItem value="compliance">Compliance Issues</SelectItem>
              <SelectItem value="courses">Total Courses</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Members List */}
        <motion.div variants={item} className="space-y-2">
          {filtered.length === 0 ? (
            <Card className="border"><CardContent className="p-8 text-center text-muted-foreground">No team members found</CardContent></Card>
          ) : (
            filtered.map(m => (
              <Card key={m.id} className="border hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate(`/manager/team/${m.id}`)} data-testid={`team-member-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-11 w-11 border-2 border-primary/10">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">{getInitials(m.full_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">{m.full_name}</p>
                        <Badge variant={m.status === "active" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">{m.status}</Badge>
                        {m.compliance_overdue > 0 && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0 gap-1">
                            <AlertTriangle className="w-3 h-3" /> {m.compliance_overdue} overdue
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{m.job_title || m.email} {m.department ? `· ${m.department}` : ""}</p>
                    </div>

                    <div className="hidden md:flex items-center gap-6 text-center">
                      <div>
                        <p className="text-lg font-bold">{m.total_courses}</p>
                        <p className="text-[10px] text-muted-foreground">Courses</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold">{m.completed_courses}</p>
                        <p className="text-[10px] text-muted-foreground">Completed</p>
                      </div>
                      <div className="w-24">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium">{m.completion_rate}%</p>
                        </div>
                        <Progress value={m.completion_rate} className="h-1.5" />
                      </div>
                      <div>
                        <p className="text-lg font-bold">{m.compliance_rate}%</p>
                        <p className="text-[10px] text-muted-foreground">Compliance</p>
                      </div>
                    </div>

                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </motion.div>
      </motion.div>
    </AdminLayout>
  );
};

export default ManagerTeamPage;
